import classNames from 'classnames'
import { MdAdd, MdContentCopy } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IABTestVariation } from '~/types/arbitration'

import { ABTestVariationBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import CircularProgress from '~/components/CircularProgress'
import Label from '~/components/Label'
import { copyText } from '~/utils/navigator'
import { truncate } from '~/utils/string'

import './styles.scss'

interface Props {
  className?: string
  navigationGraphOptions: IOption[]
  onEdit?: VoidFunction
  variations: IABTestVariation
}

const ABTestVariations = ({ className, navigationGraphOptions, onEdit, variations }: Props): ReactElement => {
  const controlVariation = variations.composition.control_variation

  const navigationGraphOption = navigationGraphOptions.find(({ value }) => value === controlVariation?.id) as IOption

  const controlVariationOption = { traffic_share: controlVariation?.traffic_share, ...navigationGraphOption }

  const testedVariations =
    variations.composition.tested_variations
      ?.filter(({ id }) => !!navigationGraphOptions.find(({ value }) => value === id))
      .map(({ id, traffic_share }) => {
        const navigationGraphOption = navigationGraphOptions.find(({ value }) => value === id) as IOption
        return { traffic_share, ...navigationGraphOption }
      }) || []

  const handleNavigationGraphLinkClick = (id: string) => {
    window.open(`/dynamic-screens/navigation-graphs/${id}`, '_blank')
  }

  return (
    <div className={classNames('ab-test-variations', className)}>
      <div className="ab-test-variations__row">
        {controlVariation ? (
          <>
            <div className="data-container">
              <CircularProgress value={controlVariation.traffic_share} />
              <ABTestVariationBadge value="control" />
              <Label label={controlVariationOption.label} />
            </div>
            <div className="actions-container">
              <LinkButton
                backgroundColor="light"
                label={truncate(controlVariation.id)}
                onClick={() => handleNavigationGraphLinkClick(controlVariation.id)}
                type="external"
              />
              <Button
                ghost
                onClick={() => copyText(controlVariation.id, 'Navigation graph ID copied to clipboard.')}
                type="button"
              >
                <MdContentCopy size={14} />
              </Button>
            </div>
          </>
        ) : onEdit ? (
          <Button onClick={onEdit} type="button">
            <MdAdd color="#fff" size={16} />
            Variations
          </Button>
        ) : (
          <div className="pool-created-label">Pool created</div>
        )}
      </div>
      {testedVariations?.map((navigationGraph, index) => (
        <div className="ab-test-variations__row" key={index}>
          <div className="data-container">
            <CircularProgress value={navigationGraph.traffic_share} variant="secondary" />
            <ABTestVariationBadge value="variation" />
            <Label label={navigationGraph.label} />
          </div>
          <div className="actions-container">
            <LinkButton
              backgroundColor="light"
              label={truncate(navigationGraph.value)}
              onClick={() => handleNavigationGraphLinkClick(navigationGraph.value)}
              type="external"
            />
            <Button
              ghost
              onClick={() => copyText(navigationGraph.value, 'Navigation graph ID copied to clipboard.')}
              type="button"
            >
              <MdContentCopy size={14} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default ABTestVariations
